/**
 * Named role sets for the `AppRoutes` guards that were previously anonymous inline
 * literals (PRO-1360 / ADR-014 A14).
 *
 * Every other guarded route tree already spreads an exported constant
 * (`INSIGHTS_ROUTE_ROLES`, `REFERRAL_ROLES`, `COMMERCIAL_DATA_ROLES`, `ATR_ROLES` …).
 * These eight did not, which mattered once chrome had to be provably a subset of what
 * the guards admit: a parity test cannot compare against an anonymous literal, and a
 * hand-copied table can silently drift into agreement with a bug — which is exactly how
 * the `commercial-sites-projection` nav/guard divergence survived.
 *
 * This module is a pure extraction. No role set changes value; `AppRoutes`, the module
 * gate table in `modulesConfig` and the parity suite now read the same constants, so a
 * future guard change cannot leave chrome behind.
 */
import type { StaffRole } from '@ucp/web/lib';

/** `/compose/*` and `/scheduled` (`AppRoutes.tsx`). Capability: `compose_send`. */
export const COMPOSE_ROLES: readonly StaffRole[] = ['L1_SENDER'];

/** `/approvals`. Capability: `approve_broadcast`. */
export const APPROVALS_ROLES: readonly StaffRole[] = ['L2_APPROVER'];

/** `/platform/*`, `/settings/*` and `/ops` — the portal-ops tier (ADR-014 A5). */
export const PLATFORM_ADMIN_ROLES: readonly StaffRole[] = ['L6_PLATFORM_ADMIN'];

/** `/settings/school-templates` (ADR-024 — school-owned template authoring). */
export const SCHOOL_TEMPLATE_ROLES: readonly StaffRole[] = [
  'L2_APPROVER',
  'PRINCIPAL',
  'OPS_DIRECTOR',
];

/** `/complaints/*`. Capability: `complaint_record`. */
export const COMPLAINTS_ROUTE_ROLES: readonly StaffRole[] = ['COMPLAINT_HANDLER', 'OPS_DIRECTOR'];
